import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const UserIcon = ({ className = "h-6 w-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const PrincipalLetterPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 font-sans selection:bg-cyan-200 selection:text-blue-900">
      <Navbar />

      <main id="main-content">
        {/* Hero Section */}
        <section className="relative flex min-h-[50vh] items-center justify-center overflow-hidden bg-blue-900">
          {/* Background with Gradient Overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1920&q=80&auto=format"
              alt="Students at River East Elementary"
              className="h-full w-full object-cover opacity-30 mix-blend-overlay"
              loading="eager"
            />
            <div className="absolute inset-0 bg-blue-950/75" />
          </div>

          {/* Content */}
          <div className="relative z-10 mx-auto max-w-7xl px-6 py-24 text-center sm:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-5xl font-bold tracking-tighter text-white sm:text-6xl md:text-7xl">
                Principal's <br />
                <span className="text-cyan-400">Letter</span>
              </h1>
              <p className="mt-6 text-xl font-medium leading-relaxed text-blue-50 sm:text-2xl">
                A message from Principal Michael Panetta
              </p>
            </div>
          </div>
        </section>

        {/* Letter Content Section */}
        <section className="py-24 sm:py-32 bg-white">
          <div className="mx-auto max-w-5xl px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 text-blue-700 mb-6">
                <UserIcon className="h-8 w-8" />
              </div>
              <h2 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-6">
                February 2, 2026
              </h2>
            </div>

            <div className="rounded-3xl bg-blue-50 p-8 sm:p-12 shadow-xl ring-1 ring-gray-900/5">
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-900 font-semibold mb-6 text-lg">Dear River East Families,</p>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  On Friday, we held a parent meeting to discuss student attendance and NYCDOE promotion
                  criteria. If you were not able to attend the meeting, we discussed the following:
                </p>

                <ul className="list-disc pl-6 space-y-4 text-gray-700 mb-8 leading-relaxed">
                  <li>
                    Our year-to-date attendance is 89% and 46% of students are considered chronically absent
                    (missing 10% or more of the school year). In addition, we average between 50-75 students late
                    every day. Regular attendance is crucial for students to receive the social-emotional and
                    academic support they need to be successful. Missing even 2 days per month can have a lasting
                    negative impact. You can track your child's attendance in your NYC Schools Account.
                  </li>
                  <li>
                    By law, schools must take attendance every day. We can mark an absence as "excused" for
                    religious reasons, illness, or some other reasons, but they are still absences and are part of the
                    student's record. We are required to contact families when students are absent, as well as if they
                    are late arriving to school. Parents must provide a reason for absences.
                  </li>
                  <li>
                    If your child is missing school due to transportation problems, health issues, or if your child does
                    not want to come to school, we may have school resources that can help you and your child.
                  </li>
                  <li>
                    If you were notified by your child's teacher that they are "promotion in doubt," this is a formal
                    notification stating that a student is at risk of not meeting grade-level standards and may be retained
                    or mandated to attend Summer Rising. In some cases, students who are consistently absent or late
                    are missing so much work that the teacher is unable to assess whether or not they are performing on-
                    grade level. If you have questions about your child's promotion status, please reach out to me or
                    your child's teacher immediately.
                  </li>
                </ul>

                <p className="text-gray-900 font-semibold mb-4 text-lg">Additional Announcements:</p>

                <ul className="list-disc pl-6 space-y-4 text-gray-700 mb-8 leading-relaxed">
                  <li>
                    Thank you to our PTA for planning a festive Winter Gala! The PTA has also planned a Wellness
                    Wednesday Yoga Workshop on February 11<sup>th</sup> at 9:00 AM and a Photo Booth Friday for students on
                    February 13<sup>th</sup>. We will share an updated Family calendar with these events included. In
                    addition, our Title 1 Committee has planned a Family Cooking class on March 25<sup>th</sup> (2:40-4:40 PM)
                    and Family Art Classes on April 29<sup>th</sup> and May 29<sup>th</sup> (2:40-3:40 PM). A sign-up sheet will be shared
                    closer to the date of the event.
                  </li>
                  <li>
                    Parent-Teacher conferences are scheduled for Thursday, March 5<sup>th</sup>. Your child's teacher will send
                    home a sign-up sheet when we return from Winter Break. Please note that this is a half-day for
                    students (11:20 AM Dismissal). You can review your child's report card from the fall and their most
                    up-to-date assessment information in your NYC Schools Account.
                  </li>
                </ul>

                <p className="text-gray-700 mb-8 leading-relaxed">
                  Thank you for your continued partnership and I wish you and your family a restful Mid-Winter
                  Break!
                </p>

                <p className="text-gray-900 mb-2 text-lg">Sincerely,</p>

                <div className="mb-4">
                  <img
                    src="/assets/images/principal letter/signature.png"
                    alt="Michael Panetta's Signature"
                    className="h-20 object-contain"
                  />
                </div>

                <p className="text-gray-900 font-semibold text-lg">Michael Panetta</p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="bg-blue-900 py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Have Questions or Concerns?
              </h2>
              <p className="mt-4 text-lg leading-8 text-blue-100">
                We're here to support your child's success. Reach out to us anytime.
              </p>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <a
                  href="mailto:mpanetta@schools.nyc.gov"
                  className="rounded-full bg-white px-8 py-4 text-base font-semibold text-blue-900 shadow-xl transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  Contact Principal
                </a>
                <a
                  href="/"
                  className="text-base font-semibold leading-7 text-white hover:text-cyan-400 transition-colors"
                >
                  Back to Home <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default PrincipalLetterPage;
